import {
  IPage,
  ISection,
  ISectionRule,
  ITemplate,
  TemplateContent,
} from "interfaces";
import _, { isArray } from "lodash";

import { searchValues } from "components/TemplateEditor/Functions";
import { RulePrefix } from "constants/TemplateEditor";
import { SectionValidationError } from "../Interfaces";
import { Operators } from "../Constants";

/**
 * @description - Check whether the given string is a valid numeric value or not
 * @param {string} value - Input string value
 * @returns {boolean}
 */
export const isNumeric = (value: string): boolean => {
  try {
    if (value) {
      return !isNaN(Number(value)) && !isNaN(parseFloat(value));
    }
    return false;
  } catch (error) {
    return false;
  }
};

/**
 * @description - Create rule condition from the inputs
 * @returns {string} - Generated rule with exact format
 */
export const generateRuleCondition = ({
  leftOperand,
  operator,
  rightOperand,
  conjunction,
  parameters,
}: {
  leftOperand: string;
  operator: Operators;
  rightOperand: string;
  conjunction: string;
  parameters: any[];
}): string => {
  try {
    rightOperand = rightOperand ? rightOperand : "";
    const parameter = parameters.find(
      (parameter) => parameter.tokenValue == rightOperand
    );

    const isNumericRightOperand = isNumeric(rightOperand);
    const isActivityCode = leftOperand === "ActivityCode";
    const isNumericOperator = [
      Operators.GREATER_THAN_OR_EQUL,
      Operators.LEASS_THAN_OR_EQUAL,
    ].includes(operator);

    const shouldHandleAsNumeric =
      isNumericRightOperand && (!isActivityCode || isNumericOperator);

    if (parameter) {
      rightOperand = parameter.tokenValue;
    } else {
      if (rightOperand) {
        rightOperand = rightOperand = shouldHandleAsNumeric
          ? `${rightOperand}`
          : `\"${rightOperand}\"`;
      }
    }

    const condition = {
      leftOperand: leftOperand ? leftOperand : "",
      operator: operator ? operator : "",
      rightOperand: rightOperand,
      conjunction: conjunction ? conjunction : "",
    };
    const ruleExpression = Object.values(condition)
      .toString()
      .replaceAll(",", " ");
    return ruleExpression;
  } catch (error) {
    console.error(error);
    return "";
  }
};

/**
 * @description - Create rule condition from the inputs
 * @returns {string} - Generated rule with exact format
 */
export const generateRuleExpression = ({
  leftOperand,
  operator,
  rightOperand,
  conjunction,
  parameters,
}: {
  leftOperand: string;
  operator: Operators;
  rightOperand: string;
  conjunction: string;
  parameters: any[];
}): string => {
  try {
    const parameter = parameters.find(
      (parameter) => parameter.tokenValue == rightOperand
    );

    let rulePrefix = RulePrefix.DEFAULT;
    const isNumericRightOperand = isNumeric(rightOperand);
    const isActivityCode = leftOperand === "ActivityCode";
    const isNumericOperator = [
      Operators.GREATER_THAN_OR_EQUL,
      Operators.LEASS_THAN_OR_EQUAL,
    ].includes(operator);

    if (
      (isNumericRightOperand && !isActivityCode) ||
      (isNumericOperator && isNumericRightOperand)
    ) {
      rulePrefix = RulePrefix.NUMERIC;
    }

    if (parameter) {
      rightOperand = parameter.tokenValue;
    } else {
      rightOperand = rightOperand =
        (isNumericRightOperand && !isActivityCode) ||
        (isNumericOperator && isNumericRightOperand)
          ? `${rightOperand}`
          : `\"${rightOperand}\"`;
    }

    const condition = {
      leftOperand: rulePrefix + leftOperand,
      operator,
      rightOperand,
      conjunction: conjunction ? ` ${conjunction}` : "",
    };
    return Object.values(condition).toString().replaceAll(",", "");
  } catch (error) {
    console.error(error);
    return "";
  }
};

export const onlyNumbers = (str: string): boolean => {
  try {
    return /^[0-9]+$/.test(str);
  } catch (error) {
    return false;
  }
};

export const getUSCSectionStyleClass = (
  name: any,
  section: any,
  isHidden: boolean
) => {
  try {
    if (section) {
      if (name == section.name) {
        if (isHidden) {
          return " lock-section ";
        } else {
          return " lock-section active";
        }
      }
      return "";
    }
  } catch (error) {
    return "";
  }
};

/**
 * @description - Create rule condition from the inputs
 * @param element - Parameter value
 * @param params - Parameter list
 * @returns - Parameter description
 */
export const getParamTooltipTitle = (element: any, params: Array<any>) => {
  try {
    if (element?.length > 0 && Array.isArray(params)) {
      return _.find(params, { tokenValue: element[0]?.data })?.tokenName;
    }
    return "";
  } catch (error) {
    console.error(error);
    return "";
  }
};

/**
 * @description Update repeating adapter for the given section
 * @param {ITemplate} template - Current template
 * @param {string} sectionName - Section name to update
 * @param {any} newSectionData - Update section details
 */
export const updateSectionByName = (
  template: ITemplate,
  sectionName: string,
  newSectionData: any
): ITemplate => {
  try {
    const pageIndex = template.templateContent.pages.findIndex((page) =>
      page.sections.some((section) => section.name == sectionName)
    );
    if (pageIndex > -1) {
      const sectionIndex = template.templateContent.pages[
        pageIndex
      ].sections.findIndex((section) => section.name == sectionName);
      if (sectionIndex > -1) {
        const sectionToUpdate =
          template.templateContent.pages[pageIndex].sections[sectionIndex];

        template.templateContent.pages[pageIndex].sections[sectionIndex] = {
          ...sectionToUpdate,
          ...newSectionData,
        };
      }
    }

    return template;
  } catch (error) {
    return template;
  }
};

/**
 * @description - Generate tree data structure to display repeating adapters
 * @param {any[]} adapters - Repeating adapters
 * @param {string | undefined} searchText - Search text
 */
export const generateTreeData = (adapters: any[], searchText?: string) => {
  try {
    if (adapters && isArray(adapters)) {
      const treeData = adapters.map((adapter) => {
        if (adapter != undefined) {
          const dataTokens = isArray(adapter.dataTokens)
            ? adapter.dataTokens?.map((dataToken: any) => {
                return {
                  ...dataToken,
                  checkable: false,
                  isLeaf: true,
                };
              })
            : [];
          return {
            ...adapter,
            tokenName: adapter.adapterName,
            tokenValue: adapter.adapterId,
            dataTokens: searchText
              ? searchValues(dataTokens, searchText, "tokenName")
              : dataTokens,
          };
        }
      });
      return treeData;
    } else {
      return [];
    }
  } catch (error) {
    console.error("generateTreeData failed : ", error);
    return [];
  }
};

/**
 * @description Check whether all sections is locked or not
 * @param {TemplateContent} templateContent - Template Content of the template
 * @returns {boolean}
 */
export const getLockAllStatus = (templateContent: TemplateContent): boolean => {
  try {
    if (templateContent && isArray(templateContent.pages)) {
      return templateContent.pages.every((page: IPage) => {
        return page.sections.every((section: ISection) => section.locked);
      });
    } else {
      return false;
    }
  } catch (error) {
    console.error("getLockAllStatus failed => ", error);
    return false;
  }
};

/**
 * @description Get section HTML content by rule
 * @param {string} rule - Template section rule
 * @param {ISection} section - Template section
 * @returns {string}
 */
export const getContentByRule = (rule: string, section: ISection): string => {
  try {
    if (section) {
      if (rule) {
        // return matching rule content
        const matchingRule = section.rules.find(
          (sectionRule: ISectionRule) => sectionRule.rule == rule
        );
        if (matchingRule) {
          return matchingRule.content;
        } else {
          // return default rule content
          const defaultRule = section.rules.find(
            (sectionRule: ISectionRule) => sectionRule.isDefault
          );
          return defaultRule?.content ?? "";
        }
      } else {
        // return default rule content
        const defaultRule = section.rules.find(
          (sectionRule: ISectionRule) => sectionRule.isDefault
        );
        return defaultRule?.content ?? "";
      }
    } else {
      return "";
    }
  } catch (error) {
    return "";
  }
};

/**
 * @description Delete section from the template
 * @param {ITemplate} template - Current template
 * @param {string} sectionName - Section name to update
 */
export const deleteSectionByName = (
  template: ITemplate,
  sectionName: string
): ITemplate => {
  try {
    let updatedTemplate = template;
    // Iterate through pages and sections to find and remove the section by name
    updatedTemplate.templateContent.pages.forEach((page) => {
      page.sections = page.sections.filter(
        (section) => section.name !== sectionName
      );
    });
    return updatedTemplate;
  } catch (error) {
    return template;
  }
};

/**
 * @description Validate section content
 * @param {string} id - Section Id
 */
export const validateSection = (id: string): Array<SectionValidationError> => {
  try {
    let errors: Array<SectionValidationError> = [];
    const sectionElement = document.getElementById(id);
    if (sectionElement) {
      if (
        sectionElement.hasAttribute("sectionname") &&
        sectionElement.hasAttribute("rule")
      ) {
        if (sectionElement.hasAttribute("repeatingadapterid")) {
          const repeatingContent = sectionElement?.querySelector(
            "tr[id=repeatingcontent]"
          );
          if (repeatingContent) {
            if (
              !repeatingContent.hasAttribute("repeatingadapterid") ||
              repeatingContent.getAttribute("repeatingadapterid") == "0"
            ) {
              errors.push({
                code: 4301,
                error:
                  "US.USC.TEMPLATEEDITOR:VALIDATIONS.NO_REPEATING_ADAPTER_SETUP",
              });
            }
          }
        }
      } else {
        errors.push({
          code: 4302,
          error: "US.USC.TEMPLATEEDITOR:VALIDATIONS.SECTION_RULE_NOT_FOUND",
        });
      }
    }
    return errors;
  } catch (error) {
    return [];
  }
};

/**
 * @description Validate section name is duplicate or not
 * @param {ITemplate} template - Current template
 * @param {string} sectionName - Section name to update
 */
export const isDuplicateSectionName = (
  template: ITemplate,
  sectionName: string | undefined | null
): boolean => {
  try {
    let isDuplicate = false;
    // Iterate through pages and sections to find and remove the section by name
    template.templateContent.pages.forEach((page) => {
      page.sections.forEach((section) => {
        if (section.name == sectionName?.trim()) {
          isDuplicate = true;
          return;
        }
      });
    });
    return isDuplicate;
  } catch (error) {
    return false;
  }
};

/**
 * @description Validate whether the warning message should display or not
 * @param {number} templateVersion - template version number
 * @param {number} historyLatestVersion - history latest version number
 */
export const isHistoryWarningVisible = (
  templateVersion: any,
  historyLatestVersion: any
): boolean => {
  try {
    if (templateVersion || historyLatestVersion) {
      if (
        templateVersion != historyLatestVersion &&
        templateVersion > historyLatestVersion
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

/**
 * @description - Check if the given markup is a break markup
 * @param {string} markup - HTML string
 * @returns {boolean} - True if the markup is a break markup, otherwise false
 */
export const isBreakMarkup = (markup: string): boolean => {
  try {
    const normalized = markup
      .replace(/&nbsp;/gi, "")
      .replace(/\s+/g, "")
      .toLowerCase();
    if (!normalized) {
      return true;
    }
    if (normalized.startsWith("<br")) {
      return true;
    }
    return normalized === "<br></br>";
  } catch (error) {
    return false;
  }
};

/**
 * @description - Convert SMS template content to editor content
 * @param {string} rawContent - Raw HTML content of the SMS template
 * @returns {string} - Transformed HTML content suitable for the editor
 */
export const smsTemplateToEditorContent = (rawContent?: string): string => {
  if (!rawContent) {
    return "";
  }

  try {
    const domParser = new DOMParser();
    const parsedDocument = domParser.parseFromString(rawContent, "text/html");
    const container =
      (parsedDocument.body.firstElementChild as HTMLElement) ??
      parsedDocument.body;

    const nodes = Array.from(container.childNodes);
    let requiresTransformation = false;
    const fragments: string[] = [];

    nodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.tagName === "SPAN") {
          requiresTransformation = true;
          const inner = element.innerHTML ?? "";
          if (isBreakMarkup(inner)) {
            fragments.push("<p><br></p>");
          } else {
            fragments.push(`<p>${inner}</p>`);
          }
        } else if (element.tagName === "BR") {
          requiresTransformation = true;
          fragments.push("<p><br></p>");
        } else {
          fragments.push(element.outerHTML);
        }
      } else if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          fragments.push(`<p>${text}</p>`);
        }
      }
    });

    if (!requiresTransformation || fragments.length === 0) {
      return rawContent;
    }

    if (container.tagName === "BODY") {
      return fragments.join("");
    }

    const clonedContainer = container.cloneNode(false) as HTMLElement;
    clonedContainer.innerHTML = fragments.join("");
    return clonedContainer.outerHTML;
  } catch (error) {
    return rawContent;
  }
};

/** * @description - Convert editor content to SMS template content
 * @param {string} editorContent - HTML content from the editor
 * @returns {string} - Transformed HTML content suitable for the SMS template
 */
export const editorContentToSmsTemplate = (editorContent: string): string => {
  if (!editorContent) {
    return editorContent;
  }

  try {
    const domParser = new DOMParser();
    const parsedDocument = domParser.parseFromString(
      `<wrapper>${editorContent}</wrapper>`,
      "text/html"
    );
    const wrapper =
      (parsedDocument.body.firstElementChild as HTMLElement) ??
      parsedDocument.body;

    const paragraphs = Array.from(wrapper.querySelectorAll("p"));

    if (paragraphs.length === 0) {
      return editorContent;
    }

    const fragments: string[] = [];

    paragraphs.forEach((paragraph) => {
      const inner = paragraph.innerHTML ?? "";
      if (isBreakMarkup(inner)) {
        fragments.push("<span><br></span>");
      } else {
        fragments.push(`<span>${inner}</span>`);
        fragments.push("<span><br></span>");
      }
    });

    while (
      fragments.length > 0 &&
      fragments[fragments.length - 1] === "<span><br></span>"
    ) {
      fragments.pop();
    }

    return fragments.join("") || editorContent;
  } catch (error) {
    return editorContent;
  }
};
